import { BREAKPOINTS } from "./breakpoints";

const theme = {
  colors: {
    // Palette — raw shade values
    accent: {
      900: "#0E0C1F",
      800: "#15122D",
      700: "#1B173A",
      600: "#211C47",
      500: "#292359",
      400: "#544F7A",
      300: "#7F7B9B",
      200: "#A9A7BD",
      150: "#BFBDCD",
      100: "#D4D3DE",
      50: "#F4F4F7",
    },
    primary: {
      900: "#000000",
      800: "#1C1C1C",
      700: "#383838",
      600: "#555555",
      500: "#717171",
      400: "#8E8E8E",
      300: "#AAAAAA",
      200: "#C7C7C7",
      150: "#D5D5D5",
      100: "#E3E3E3",
      50: "#FFFFFF",
    },
    error: {
      900: "#4A0A0A",
      800: "#6B0F0F",
      700: "#8C1414",
      600: "#A31717",
      500: "#BA1A1A",
      400: "#C84848",
      300: "#D67676",
      200: "#E4A3A3",
      150: "#EBBABA",
      100: "#F1D1D1",
      50: "#F8E8E8",
    },
    success: {
      900: "#1E4A1B",
      800: "#2D6B28",
      700: "#3B8C35",
      600: "#43A03C",
      500: "#4BB543",
      400: "#6FC469",
      300: "#93D38E",
      200: "#B7E2B4",
      150: "#C9EAC7",
      100: "#DBF2DA",
      50: "#EDF9EC",
    },
    warn: {
      900: "#4A2A03",
      800: "#6B3C05",
      700: "#9E5A07",
      600: "#C57712",
      500: "#E89426",
      400: "#EFA950",
      300: "#F4BD7A",
      200: "#F9D2A3",
      150: "#FBDDB5",
      100: "#FFE9C7",
      50: "#FFF7EB",
    },
    // Semantic — design intent tokens
    semantic: {
      accent: {
        default: "#292359",
        hover: "#211C47",
        pressed: "#0E0C1F",
        disabled: "#E3E3E3",
        onAccent: "#FFFFFF",
      },
      text: {
        strong: "#000000",
        weak: "#8E8E8E",
        placeholder: "#AAAAAA",
        disabled: "#8E8E8E",
        error: "#BA1A1A",
        link: "#292359",
      },
      icon: {
        strong: "#000000",
        weak: "#8E8E8E",
        placeholder: "#AAAAAA",
        disabled: "#C7C7C7",
        error: "#BA1A1A",
      },
      container: {
        white: "#FFFFFF",
        whiteHover: "#E3E3E3",
        whitePressed: "#E3E3E3",
        gray: "#E3E3E3",
        grayHover: "#D5D5D5",
        grayPressed: "#C7C7C7",
      },
      background: {
        white: "#FFFFFF",
        gray: "#E3E3E3",
      },
      outline: {
        default: "#C7C7C7",
        hover: "#8E8E8E",
        pressed: "#717171",
      },
      separator: "#E3E3E3",
      status: {
        success: "#EDF9EC",
        onSuccess: "#43A03C",
        error: "#F8E8E8",
        onError: "#BA1A1A",
      },
    },
  },

  typography: {
    fontFamily: '"Inter", sans-serif',
    display: {
      d1: { size: "60px", lineHeight: "72px", weight: 400 },
      d2: { size: "48px", lineHeight: "56px", weight: 400 },
    },
    heading: {
      h1: { size: "28px", lineHeight: "32px", weight: 400 },
      h2: { size: "32px", lineHeight: "40px", weight: 400 },
      h3: { size: "24px", lineHeight: "32px", weight: 400 },
    },
    body: {
      b1: { size: "16px", lineHeight: "24px", weight: 400 },
      b2: { size: "14px", lineHeight: "20px", weight: 400 },
      b3: { size: "12px", lineHeight: "16px", weight: 400 },
    },
    title: {
      t1: { size: "16px", lineHeight: "20px", weight: 500 },
      t2: { size: "14px", lineHeight: "20px", weight: 500 },
      t3: { size: "12px", lineHeight: "12px", weight: 500 },
    },
    caption: {
      c1: { size: "14px", lineHeight: "18px", weight: 500 },
      c2: { size: "14px", lineHeight: "16px", weight: 400 },
    },
    link: { size: "14px", lineHeight: "18px", weight: 400 },
    label: { size: "12px", lineHeight: "16px", weight: 400 },
    value: { size: "14px", lineHeight: "24px", weight: 400 },
  },

  spacing: {
    4: "4px",
    8: "8px",
    12: "12px",
    16: "16px",
    20: "20px",
    24: "24px",
    32: "32px",
    40: "40px",
    48: "48px",
    56: "56px",
    64: "64px",
    80: "80px",
    120: "120px",
  },

  radius: {
    4: "4px",
    8: "8px",
    12: "12px",
    16: "16px",
    24: "24px",
    rounded: "400px",
  },

  shadows: {
    card: "0px 4px 8px rgba(0,0,0,0.04), 0px 0px 2px rgba(0,0,0,0.06), 0px 0px 1px rgba(0,0,0,0.04)",
    cardAlt: "0px 4px 8px rgba(0,0,0,0.04), 0px 0px 2px rgba(0,0,0,0.06)",
    soft: "0px 4px 4px rgba(0,0,0,0.12)",
    subtle: "0px 1px 10px rgba(0,0,0,0.12)",
    dropdown: "0px 2px 4px rgba(0,0,0,0.25)",
  },

  // Breakpoints come from the shared source of truth so theme-based styled
  // components and the useMediaQuery hook agree on the same px values.
  breakpoints: BREAKPOINTS,

  // Central stacking order. Mobile shell chrome (top bar / drawer backdrop /
  // drawer) sits just under the global SideModal (1000) / CentralModal (1100).
  zIndex: {
    topBar: 1000,
    drawerBackdrop: 1001,
    drawer: 1002,
    sideModal: 1000,
    centralModal: 1100,
  },
};

export default theme;
